import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import UnitSelector from './UnitSelector';
import QuantityControl from '../../helper/QuantityControl';
import { normalizeQuantityForBackend } from '../../utils/unitHelpers';

/**
 * QuickViewModal - Modern ürün hızlı görüntüleme modal component
 */
const QuickViewModal = ({ product, isOpen, onClose }) => {
  const { addToCart } = useCart();
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedUnit, setSelectedUnit] = useState('kg');
  const [isAdding, setIsAdding] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Görsel URL - useMemo ile optimize edildi
  const imageApiUrl = useMemo(() => 
    (process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, ''),
    []
  );
  
  const imageUrl = useMemo(() => {
    if (!product?.imageUrl) return "assets/images/logo/logo.png";
    return product.imageUrl.startsWith('http')
      ? product.imageUrl
      : `${imageApiUrl}${product.imageUrl}`;
  }, [product?.imageUrl, imageApiUrl]);

  // İndirim hesaplama - useMemo ile optimize edildi
  const hasDiscount = useMemo(() => 
    product?.discountedPrice && product.discountedPrice < product.price,
    [product?.discountedPrice, product?.price]
  );
  
  const discountPercentage = useMemo(() => 
    hasDiscount 
      ? Math.round((1 - product.discountedPrice / product.price) * 100) 
      : 0,
    [hasDiscount, product?.discountedPrice, product?.price]
  );
  
  const currentPrice = useMemo(() => 
    product?.discountedPrice || product?.price || 0,
    [product?.discountedPrice, product?.price]
  );

  // Modal açıldığında initial değerleri ayarla
  useEffect(() => {
    if (isOpen && product) {
      if (product.saleType === 1) {
        setSelectedQuantity(1); // 1kg ile başla
        setSelectedUnit('kg');
      } else {
        setSelectedQuantity(1);
        setSelectedUnit('piece');
      }
      setShowSuccess(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product?.saleType, product?.id]);

  // Miktar değişimi - useCallback ile optimize edildi
  const handleQuantityChange = useCallback((quantity, unit) => {
    setSelectedQuantity(quantity);
    setSelectedUnit(unit);
  }, []);

  // Adet bazlı ürünler için miktar değişimi - useCallback ile optimize edildi
  const handlePieceQuantityChange = useCallback((quantity) => {
    setSelectedQuantity(quantity);
  }, []);

  // Sepete ekle - useCallback ile optimize edildi
  const handleAddToCart = useCallback(async () => {
    if (!product || isAdding) return;

    try {
      setIsAdding(true);

      let weightGrams = null;
      if (product.saleType === 1) {
        weightGrams = normalizeQuantityForBackend(selectedQuantity, selectedUnit, product.saleType);
      }

      const result = await addToCart(
        product.id, 
        product.saleType === 1 ? 1 : selectedQuantity, 
        "", 
        weightGrams
      );

      if (result.success) {
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1200);
      }
    } catch (error) {
      console.error('Sepete ekleme hatası:', error);
    } finally {
      setIsAdding(false);
    }
  }, [product, isAdding, selectedQuantity, selectedUnit, addToCart, onClose]);

  // ESC tuşu ile kapatma - optimize edildi
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Body scroll kontrolü
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Fiyat hesaplama (gram bazlı ürünler için) - useMemo ile optimize edildi
  // Early return'den önce çağrılmalı (React Hook kuralları)
  const totalPrice = useMemo(() => {
    if (!product) return 0;
    if (product.saleType === 1) {
      // Kilogram bazlı ürün - birime göre hesapla
      let quantityInKg = selectedQuantity;
      if (selectedUnit === 'gram') {
        quantityInKg = selectedQuantity / 1000;
      }
      return currentPrice * quantityInKg;
    }
    return currentPrice * selectedQuantity;
  }, [product, currentPrice, selectedQuantity, selectedUnit]);

  if (!isOpen || !product) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="quick-view-overlay"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          animation: 'fadeIn 0.2s ease'
        }}
      />

      {/* Modal */}
      <div 
        className="quick-view-modal"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '24px',
          maxWidth: '1000px',
          width: '92%',
          maxHeight: '90vh',
          overflowY: 'auto',
          zIndex: 9999,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          animation: 'modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Kapatma Butonu */}
        <button
          onClick={onClose}
          className="close-modal-btn"
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#f9fafb',
            color: '#6b7280',
            fontSize: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#ef4444';
            e.target.style.color = 'white';
            e.target.style.transform = 'rotate(90deg)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.color = '#6b7280';
            e.target.style.transform = 'rotate(0deg)';
          }}
        >
          <i className="ph ph-x"></i>
        </button>

        {/* Modal İçeriği */}
        <div className="modal-content p-40 p-sm-24">
          <div className="row g-40">
            {/* Sol Taraf - Ürün Görseli */}
            <div className="col-lg-5">
              <div className="product-image-wrapper" style={{ position: 'relative' }}>
                {/* Badges */}
                <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 2, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {hasDiscount && (
                    <span 
                      style={{
                        backgroundColor: '#ef4444',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                        display: 'inline-block'
                      }}
                    >
                      %{discountPercentage} İNDİRİM
                    </span>
                  )}
                  {product.isNew && (
                    <span 
                      style={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                        display: 'inline-block'
                      }}
                    >
                      YENİ
                    </span>
                  )}
                </div>

                <div style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '20px',
                  padding: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '450px'
                }}>
                  <img
                    src={imageUrl}
                    alt={product.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "assets/images/logo/logo.png";
                    }}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'contain',
                      filter: product.isOutOfStock ? 'grayscale(100%)' : 'none',
                      opacity: product.isOutOfStock ? 0.5 : 1
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Sağ Taraf - Ürün Detayları */}
            <div className="col-lg-7">
              <div className="product-details" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Ürün Adı */}
                <h2 style={{ 
                  fontSize: '32px', 
                  fontWeight: '700', 
                  color: '#111827', 
                  marginBottom: '16px',
                  lineHeight: '1.2'
                }}>
                  {product.name}
                </h2>

                {/* Rating & Reviews */}
                {product.rating > 0 && (
                  <div className="d-flex align-items-center gap-12 mb-20">
                    <div className="d-flex">
                      {[...Array(5)].map((_, index) => (
                        <i 
                          key={index}
                          className={`ph-fill ph-star ${index < Math.round(product.rating) ? 'text-warning' : 'text-gray-300'}`}
                          style={{ fontSize: '20px', marginRight: '2px' }}
                        ></i>
                      ))}
                    </div>
                    <span style={{ fontSize: '15px', color: '#6b7280', fontWeight: '500' }}>
                      {product.rating.toFixed(1)} ({product.reviewCount || 0} değerlendirme)
                    </span>
                  </div>
                )}

                {/* Kısa Açıklama */}
                {product.shortDescription && (
                  <p style={{ 
                    fontSize: '16px', 
                    color: '#6b7280', 
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    {product.shortDescription}
                  </p>
                )}

                {/* Fiyat */}
                <div style={{
                  backgroundColor: '#f0fdf4',
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '32px',
                  border: '2px solid #10b981'
                }}>
                  {hasDiscount ? (
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280', textDecoration: 'line-through' }}>
                          ₺{product.price.toFixed(2)}
                        </span>
                      </div>
                      <div className="d-flex align-items-baseline gap-12">
                        <span style={{ fontSize: '42px', fontWeight: '800', color: '#10b981' }}>
                          ₺{currentPrice.toFixed(2)}
                        </span>
                        <span style={{ fontSize: '16px', color: '#6b7280' }}>
                          / {product.saleType === 1 ? 'Kg' : 'Adet'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-baseline gap-12">
                      <span style={{ fontSize: '42px', fontWeight: '800', color: '#10b981' }}>
                        ₺{currentPrice.toFixed(2)}
                      </span>
                      <span style={{ fontSize: '16px', color: '#6b7280' }}>
                        / {product.saleType === 1 ? 'Kg' : 'Adet'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Stok Durumu */}
                <div className="mb-24">
                  {product.isOutOfStock ? (
                    <div style={{
                      backgroundColor: '#fef2f2',
                      border: '2px solid #ef4444',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <i className="ph ph-x-circle" style={{ fontSize: '20px', color: '#ef4444' }}></i>
                      <span style={{ color: '#ef4444', fontWeight: '600' }}>Stokta Yok</span>
                    </div>
                  ) : product.stockQuantity > 0 && product.stockQuantity <= 10 ? (
                    <div style={{
                      backgroundColor: '#fef3c7',
                      border: '2px solid #f59e0b',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <i className="ph ph-warning" style={{ fontSize: '20px', color: '#f59e0b' }}></i>
                      <span style={{ color: '#d97706', fontWeight: '600' }}>
                        Son {product.stockQuantity} ürün!
                      </span>
                    </div>
                  ) : (
                    <div style={{
                      backgroundColor: '#f0fdf4',
                      border: '2px solid #10b981',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <i className="ph ph-check-circle" style={{ fontSize: '20px', color: '#10b981' }}></i>
                      <span style={{ color: '#059669', fontWeight: '600' }}>Stokta Var</span>
                    </div>
                  )}
                </div>

                {/* Miktar Seçici */}
                {!product.isOutOfStock && (
                  <div className="mb-32">
                    {product.saleType === 1 ? (
                      // Kilogram bazlı ürün - UnitSelector kullan
                      <UnitSelector
                        saleType={product.saleType}
                        minimumOrderWeight={product.minimumOrderWeight || 250}
                        price={0}
                        onQuantityChange={handleQuantityChange}
                        initialQuantity={1}
                        initialUnit="kg"
                        showPrice={false}
                        size="sm"
                      />
                    ) : (
                      // Adet bazlı ürün - QuantityControl kullan
                      <QuantityControl
                        initialQuantity={1}
                        onQuantityChange={handlePieceQuantityChange}
                      />
                    )}
                  </div>
                )}

                {/* Toplam Fiyat */}
                {!product.isOutOfStock && product.saleType === 1 && (
                  <div style={{
                    backgroundColor: '#eff6ff',
                    borderRadius: '16px',
                    padding: '20px 24px',
                    marginBottom: '24px',
                    border: '2px dashed #3b82f6'
                  }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>
                        Toplam Fiyat:
                      </span>
                      <span style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>
                        ₺{totalPrice.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                      {selectedQuantity} {selectedUnit === 'kg' ? 'Kg' : 'Gram'} × ₺{selectedUnit === 'kg' ? currentPrice.toFixed(2) : (currentPrice / 1000).toFixed(3)}/{selectedUnit === 'kg' ? 'Kg' : 'gr'}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ marginTop: 'auto' }}>
                  {showSuccess ? (
                    <div style={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      padding: '20px',
                      borderRadius: '16px',
                      textAlign: 'center',
                      fontSize: '18px',
                      fontWeight: '600',
                      animation: 'successPulse 0.5s ease'
                    }}>
                      <i className="ph ph-check-circle me-8" style={{ fontSize: '24px' }}></i>
                      Sepete Eklendi!
                    </div>
                  ) : (
                    <div className="d-grid gap-12">
                      <button
                        onClick={handleAddToCart}
                        disabled={isAdding || product.isOutOfStock}
                        style={{
                          backgroundColor: product.isOutOfStock ? '#e5e7eb' : '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '18px 32px',
                          borderRadius: '16px',
                          fontSize: '18px',
                          fontWeight: '700',
                          cursor: product.isOutOfStock ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          boxShadow: product.isOutOfStock ? 'none' : '0 4px 16px rgba(16, 185, 129, 0.3)',
                          transform: isAdding ? 'scale(0.98)' : 'scale(1)'
                        }}
                        onMouseEnter={(e) => {
                          if (!product.isOutOfStock && !isAdding) {
                            e.target.style.backgroundColor = '#059669';
                            e.target.style.transform = 'scale(1.02)';
                            e.target.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!product.isOutOfStock && !isAdding) {
                            e.target.style.backgroundColor = '#10b981';
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
                          }
                        }}
                      >
                        {isAdding ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-8" role="status"></span>
                            Ekleniyor...
                          </>
                        ) : product.isOutOfStock ? (
                          <>
                            <i className="ph ph-x-circle me-8"></i>
                            Stokta Yok
                          </>
                        ) : (
                          <>
                            <i className="ph ph-shopping-cart me-8"></i>
                            Sepete Ekle
                          </>
                        )}
                      </button>
                      
                      <Link
                        to={`/product-details/${product.slug || product.id}`}
                        onClick={onClose}
                        style={{
                          backgroundColor: 'white',
                          color: '#6b7280',
                          border: '2px solid #e5e7eb',
                          padding: '16px 32px',
                          borderRadius: '16px',
                          fontSize: '16px',
                          fontWeight: '600',
                          textAlign: 'center',
                          textDecoration: 'none',
                          transition: 'all 0.2s',
                          display: 'block'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#10b981';
                          e.target.style.color = '#10b981';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.color = '#6b7280';
                        }}
                      >
                        <i className="ph ph-eye me-8"></i>
                        Detaylı İncele
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -45%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        @keyframes successPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        .quick-view-modal::-webkit-scrollbar {
          width: 8px;
        }

        .quick-view-modal::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 10px;
        }

        .quick-view-modal::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }

        .quick-view-modal::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        @media (max-width: 992px) {
          .quick-view-modal {
            width: 95% !important;
            max-height: 95vh !important;
          }
          
          .quick-view-modal .modal-content {
            padding: 24px !important;
          }
          
          .quick-view-modal h2 {
            font-size: 24px !important;
          }
        }
      `}</style>
    </>
  );
};

export default QuickViewModal;
